/**
 * Shared diff utilities for comparing prompt text. Used by both the
 * SubTenantPromptEdit "Compare with Library" tab and the Library
 * Update Manager's 3-way diff view.
 */

/**
 * LCS-based line-by-line diff. Returns an array of
 * { type: "equal"|"removed"|"added", text, lineNum? } entries.
 */
export function computeDiff(oldLines, newLines) {
  const m = oldLines.length;
  const n = newLines.length;

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const result = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.push({ type: "equal", text: oldLines[i - 1], lineNum: j });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.push({ type: "added", text: newLines[j - 1], lineNum: j });
      j--;
    } else {
      result.push({ type: "removed", text: oldLines[i - 1], lineNum: i });
      i--;
    }
  }

  result.reverse();
  return result;
}

/**
 * Renders a diff as a table. Returns a React element.
 * `diff` is the output of computeDiff().
 */
export function renderDiffTable(diff, React) {
  // This is a data utility only — rendering happens in components.
  // Use computeDiff + map in JSX directly.
  return diff;
}

/**
 * Quick stat from a diff: count of added, removed, unchanged lines.
 */
export function diffStats(diff) {
  let added = 0;
  let removed = 0;
  let equal = 0;
  for (const d of diff) {
    if (d.type === "added") added++;
    else if (d.type === "removed") removed++;
    else equal++;
  }
  return { added, removed, equal };
}
